import { useState } from 'react';

interface InfographicManagerProps {
  imageUrl: string;
  onChange: (url: string) => void;
}

// Convierte cualquier URL de Google Drive al formato de imagen directa
function convertDriveUrl(url: string): string {
  if (!url) return url;

  // Formato: https://drive.google.com/file/d/FILE_ID/view...
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w2000`;
  }

  // Formato: https://drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w2000`;
  }

  // Formato: https://drive.google.com/uc?id=FILE_ID (ya convertido, actualizamos a thumbnail)
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) {
    return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w2000`;
  }

  return url;
}

export default function InfographicManager({ imageUrl, onChange }: InfographicManagerProps) {
  const [inputVal, setInputVal] = useState(imageUrl);
  const [error, setError] = useState(false);
  const [converted, setConverted] = useState(false);

  const resolvedUrl = convertDriveUrl(inputVal.trim());

  const handleChange = (val: string) => {
    setInputVal(val);
    const resolved = convertDriveUrl(val.trim());
    setConverted(resolved !== val.trim() && val.trim().length > 0);
    onChange(resolved);
    setError(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Infografía</p>
        <p className="text-[#F8F6F3]/40 text-sm">Imagen que se muestra a pantalla completa dentro de la plataforma. Sin descarga.</p>
      </div>

      {/* Instructions */}
      <div
        className="p-4 rounded-xl flex flex-col gap-3"
        style={{ background: 'rgba(139,157,131,0.06)', border: '1px solid rgba(139,157,131,0.15)' }}
      >
        <p className="text-[#8B9D83] text-xs font-semibold uppercase tracking-[2px]">Cómo obtener la URL</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5 p-3 rounded-xl" style={{ background: 'rgba(248,246,243,0.04)' }}>
            <div className="flex items-center gap-2">
              <i className="ri-google-line text-[#8B9D83] text-sm" />
              <span className="text-[#F8F6F3]/60 text-xs font-medium">Google Drive</span>
              <span className="text-[#8B9D83] text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(139,157,131,0.15)' }}>Auto-convertido</span>
            </div>
            <p className="text-[#F8F6F3]/35 text-[11px] leading-relaxed">
              1. Sube la imagen a Drive<br />
              2. Clic derecho → <strong className="text-[#F8F6F3]/50">Compartir</strong><br />
              3. Cambia a <strong className="text-[#F8F6F3]/50">"Cualquiera con el enlace"</strong><br />
              4. Copia el enlace y pégalo aquí — se convierte solo
            </p>
          </div>
          <div className="flex flex-col gap-1.5 p-3 rounded-xl" style={{ background: 'rgba(248,246,243,0.04)' }}>
            <div className="flex items-center gap-2">
              <i className="ri-image-line text-[#8B9D83] text-sm" />
              <span className="text-[#F8F6F3]/60 text-xs font-medium">Imgur / Cloudinary</span>
            </div>
            <p className="text-[#F8F6F3]/35 text-[11px] leading-relaxed">Sube la imagen a Imgur.com o Cloudinary, copia el enlace directo que termina en .jpg o .png</p>
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="flex flex-col gap-2">
        <label className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">URL de la imagen</label>
        <input
          type="text"
          value={inputVal}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="https://drive.google.com/... o enlace directo"
          className="w-full px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
          style={{
            background: 'rgba(248,246,243,0.06)',
            border: `1px solid ${error ? 'rgba(201,123,99,0.5)' : 'rgba(248,246,243,0.10)'}`,
          }}
        />
        {/* Auto-convert notice */}
        {converted && !error && (
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-[#8B9D83] text-xs" />
            <p className="text-[#8B9D83] text-[11px]">URL de Drive convertida automáticamente al formato correcto</p>
          </div>
        )}
      </div>

      {/* Preview */}
      {resolvedUrl && !error && (
        <div className="flex flex-col gap-3">
          <p className="text-[#F8F6F3]/40 text-xs uppercase tracking-[2px]">Vista previa</p>
          <div
            className="relative rounded-2xl overflow-hidden w-full"
            style={{ background: '#0D0F12', border: '1px solid rgba(248,246,243,0.08)' }}
          >
            <img
              src={resolvedUrl}
              alt="Infografía preview"
              className="w-full object-contain max-h-[480px]"
              onError={() => setError(true)}
              style={{ userSelect: 'none', pointerEvents: 'none' }}
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="absolute inset-0 select-none" style={{ userSelect: 'none' }} />
          </div>
        </div>
      )}

      {error && (
        <div className="flex flex-col gap-3">
          <div
            className="flex items-center gap-3 p-4 rounded-xl"
            style={{ background: 'rgba(201,123,99,0.08)', border: '1px solid rgba(201,123,99,0.20)' }}
          >
            <i className="ri-error-warning-line text-[#C97B63] text-sm" />
            <div className="flex flex-col gap-1">
              <p className="text-[#C97B63] text-xs">No se puede cargar la imagen.</p>
              <p className="text-[#C97B63]/60 text-[11px]">Si usas Drive, asegúrate de que el archivo está compartido como <strong>"Cualquiera con el enlace"</strong> (no solo personas específicas).</p>
            </div>
          </div>
        </div>
      )}

      {!inputVal && (
        <div
          className="flex flex-col items-center justify-center py-14 rounded-2xl gap-3"
          style={{ background: 'rgba(248,246,243,0.02)', border: '1px dashed rgba(248,246,243,0.10)' }}
        >
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-image-2-line text-[#F8F6F3]/20 text-3xl" />
          </div>
          <p className="text-[#F8F6F3]/30 text-sm">Sin infografía asignada</p>
        </div>
      )}
    </div>
  );
}
